import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Small hand-drawn copies of three member-app screens, coloured with the gym's
 * branding. They mirror the member app by hand (Home, the "Your gyms" sheet and
 * the gym page in apps/member), so when those screens change, change these too:
 * an owner picking colours against a layout members no longer see is the bug
 * this replaced.
 */

export type BrandSpot = 'logo' | 'cover' | 'primary' | 'secondary'
export type MemberScreenKey = 'home' | 'gyms' | 'page'

interface Brand {
  name: string
  logoUrl: string | null
  coverUrl: string | null
}

interface Props {
  screen: MemberScreenKey
  brand: Brand
  /** The setting being pointed at: its places on the screen get a ring. */
  highlight?: BrandSpot | null
  className?: string
}

const HIGHLIGHT = 'outline outline-[3px] outline-offset-1 outline-amber-400 rounded-[10px]'

function Spot({
  spot,
  highlight,
  className = '',
  children,
}: {
  spot: BrandSpot
  highlight?: BrandSpot | null
  className?: string
  children: ReactNode
}) {
  return (
    <div data-spot={spot} className={`${className} ${highlight === spot ? HIGHLIGHT : ''}`}>
      {children}
    </div>
  )
}

function Logo({ brand, size }: { brand: Brand; size: number }) {
  const initials = brand.name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
  return (
    <span
      className="flex flex-none items-center justify-center overflow-hidden rounded-full bg-[var(--m-brand)] text-[10px] font-bold text-[var(--m-on-brand)] ring-2 ring-[var(--m-veil)]"
      style={{ width: size, height: size }}
    >
      {brand.logoUrl ? (
        <img src={brand.logoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        initials
      )}
    </span>
  )
}

function Cover({ brand, className }: { brand: Brand; className: string }) {
  return brand.coverUrl ? (
    <img src={brand.coverUrl} alt="" className={`${className} object-cover`} />
  ) : (
    <span
      className={`${className} block bg-gradient-to-br from-[var(--m-brand)] to-[var(--m-line)]`}
    />
  )
}

function StatusBar({ light = false }: { light?: boolean }) {
  return (
    <div
      className={`flex h-5 flex-none items-center justify-between px-3 text-[8px] font-semibold ${light ? 'text-white' : ''}`}
    >
      <span>9:41</span>
      <span>●●●</span>
    </div>
  )
}

function Header({ brand, highlight }: { brand: Brand; highlight?: BrandSpot | null }) {
  const { t } = useTranslation()
  return (
    <>
      <Spot spot="secondary" highlight={highlight} className="bg-[var(--m-wash)]">
        <StatusBar />
      </Spot>
      <div className="flex items-center gap-2 bg-[var(--m-wash)] px-2.5 pb-1">
        <Spot spot="logo" highlight={highlight}>
          <Logo brand={brand} size={26} />
        </Spot>
        <div className="min-w-0">
          <p className="truncate text-[11px] font-bold">{brand.name} ▾</p>
          <span className="rounded-full bg-success-50 px-1.5 text-[8px] font-semibold text-success-700">
            ● {t('settings.branding.mock.open')}
          </span>
        </div>
      </div>
      <Spot
        spot="primary"
        highlight={highlight}
        className="flex border-b border-[var(--m-line)] bg-[var(--m-wash)]"
      >
        <span className="relative flex-1 py-1.5 text-center text-[9px] font-semibold">
          {t('settings.branding.mock.home')}
          <span className="absolute inset-x-[22%] bottom-0 h-[2px] rounded-t bg-[var(--m-brand)]" />
        </span>
        <span className="flex-1 py-1.5 text-center text-[9px] font-semibold text-foreground/40">
          {t('settings.branding.mock.classes')}
        </span>
      </Spot>
    </>
  )
}

function BottomBar({ highlight }: { highlight?: BrandSpot | null }) {
  const { t } = useTranslation()
  return (
    <Spot
      spot="primary"
      highlight={highlight}
      className="flex flex-none border-t border-[var(--m-line)] bg-white pb-2 pt-1"
    >
      {['home', 'climb', 'community', 'me'].map((k, i) => (
        <span
          key={k}
          className="relative flex flex-1 flex-col items-center gap-0.5 pt-1 text-[7.5px]"
        >
          {i === 0 && (
            <span className="absolute inset-x-[28%] -top-1 h-[2px] rounded-b bg-[var(--m-brand)]" />
          )}
          <span
            className={`h-2.5 w-2.5 rounded-[3px] border-[1.5px] ${i === 0 ? 'border-[var(--m-ink)]' : 'border-foreground/30'}`}
          />
          <span className={i === 0 ? 'font-semibold' : 'text-foreground/40'}>
            {t(`settings.branding.mock.nav.${k}`)}
          </span>
        </span>
      ))}
    </Spot>
  )
}

function HomeScreen({ brand, highlight }: { brand: Brand; highlight?: BrandSpot | null }) {
  const { t } = useTranslation()
  return (
    <>
      <Header brand={brand} highlight={highlight} />
      <Spot
        spot="secondary"
        highlight={highlight}
        className="flex flex-1 flex-col gap-2 overflow-hidden bg-[var(--m-veil)] p-2.5"
      >
        <div>
          <p className="text-[15px] font-extrabold leading-tight tracking-tight">
            {t('settings.branding.mock.greeting')}
          </p>
          <p className="text-[8.5px] text-foreground/45">{t('settings.branding.mock.passHint')}</p>
        </div>
        <div className="flex flex-col gap-1.5 rounded-xl bg-white p-2">
          <div className="flex items-center justify-between gap-1">
            <span className="truncate text-[10px] font-bold">
              {t('settings.branding.mock.plan')}
            </span>
            <Spot spot="primary" highlight={highlight}>
              <span className="rounded-full bg-[var(--m-brand)] px-1.5 py-0.5 text-[8px] font-bold text-[var(--m-on-brand)]">
                {t('settings.branding.mock.active')}
              </span>
            </Spot>
          </div>
          <span className="text-[8px] text-foreground/45">
            {t('settings.branding.mock.validUntil')}
          </span>
          <span
            aria-hidden="true"
            className="mx-auto mt-1 block aspect-square w-3/5 rounded-lg border-4 border-white bg-[length:10px_10px] outline outline-1 outline-[var(--m-line)]"
            style={{ backgroundImage: 'repeating-conic-gradient(#1c1c1f 0 25%, #fff 0 50%)' }}
          />
        </div>
      </Spot>
      <BottomBar highlight={highlight} />
    </>
  )
}

function GymsScreen({ brand, highlight }: { brand: Brand; highlight?: BrandSpot | null }) {
  const { t } = useTranslation()
  return (
    <>
      <div className="pointer-events-none opacity-40 blur-[1px]">
        <Header brand={brand} />
      </div>
      <div className="flex-1 bg-black/25" />
      <div className="absolute inset-x-0 bottom-0 top-[70px] flex flex-col gap-2 rounded-t-2xl bg-white p-2.5 shadow-lg">
        <span className="mx-auto h-1 w-7 rounded-full bg-default-200" />
        <p className="text-[13px] font-bold">{t('settings.branding.mock.yourGyms')}</p>
        <Spot
          spot="primary"
          highlight={highlight}
          className="overflow-hidden rounded-xl border-2 border-[var(--m-brand)] bg-white"
        >
          <Spot spot="cover" highlight={highlight} className="relative h-[70px]">
            <Cover brand={brand} className="h-full w-full" />
            <span className="absolute bottom-1.5 left-2 text-[11px] font-bold text-white [text-shadow:0_1px_6px_rgba(0,0,0,.5)]">
              {brand.name}
            </span>
          </Spot>
          <div className="flex justify-between px-2 py-1.5 text-[8.5px] font-semibold">
            <span className="text-success-600">● {t('settings.branding.mock.openNow')}</span>
            <span className="text-[var(--m-ink)]">{t('settings.branding.mock.details')} ›</span>
          </div>
        </Spot>
        <div className="grid grid-cols-2 gap-1.5">
          {['A', 'B'].map((k) => (
            <div key={k} className="overflow-hidden rounded-lg border border-default-200">
              <span className="block h-7 bg-gradient-to-b from-default-300 to-default-200" />
              <span className="block px-1.5 py-1 text-[8px] font-semibold text-foreground/50">
                {t('settings.branding.mock.otherGym')}
              </span>
            </div>
          ))}
        </div>
        <p className="text-center text-[9px] font-semibold text-[var(--m-ink)]">
          + {t('settings.branding.mock.findGym')}
        </p>
      </div>
    </>
  )
}

function PageScreen({ brand, highlight }: { brand: Brand; highlight?: BrandSpot | null }) {
  const { t } = useTranslation()
  return (
    <Spot
      spot="secondary"
      highlight={highlight}
      className="flex flex-1 flex-col bg-[var(--m-wash)]"
    >
      <Spot spot="cover" highlight={highlight} className="relative h-[92px] flex-none">
        <Cover brand={brand} className="absolute inset-0 h-full w-full" />
        <div className="relative">
          <StatusBar light />
        </div>
        <span className="absolute left-2 top-6 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-[10px] font-bold">
          ‹
        </span>
      </Spot>
      <div className="flex flex-col gap-1.5 px-2.5">
        <Spot spot="logo" highlight={highlight} className="-mt-5 self-start">
          <span className="block rounded-full ring-2 ring-[var(--m-brand)]">
            <Logo brand={brand} size={38} />
          </span>
        </Spot>
        <p className="text-[13px] font-bold tracking-tight">{brand.name}</p>
        <div className="flex items-baseline gap-1 text-[8.5px]">
          <span className="font-semibold text-success-600">
            ● {t('settings.branding.mock.openNow')}
          </span>
          <span className="text-foreground/45">· {t('settings.branding.mock.openUntil')}</span>
          <Spot
            spot="primary"
            highlight={highlight}
            className="ml-auto font-semibold text-[var(--m-ink)]"
          >
            {t('settings.branding.mock.allHours')} ›
          </Spot>
        </div>
        <div className="flex flex-wrap gap-1">
          <span className="rounded-full bg-white px-2 py-0.5 text-[8px] font-semibold shadow-sm">
            {t('settings.branding.mock.map')}
          </span>
          <span className="rounded-full bg-white px-2 py-0.5 text-[8px] font-semibold shadow-sm">
            {t('settings.branding.mock.directions')}
          </span>
        </div>
      </div>
    </Spot>
  )
}

/** One phone, about 190 px wide. */
export function MemberScreen({ screen, brand, highlight, className = '' }: Props) {
  return (
    <div
      className={`w-[190px] flex-none rounded-[26px] bg-neutral-900 p-1.5 shadow-lg ${className}`}
    >
      <div className="relative flex h-[372px] flex-col overflow-hidden rounded-[21px] bg-[var(--m-wash)] text-[#3f3f46]">
        {screen === 'home' && <HomeScreen brand={brand} highlight={highlight} />}
        {screen === 'gyms' && <GymsScreen brand={brand} highlight={highlight} />}
        {screen === 'page' && <PageScreen brand={brand} highlight={highlight} />}
      </div>
    </div>
  )
}
